import { lazy, ReactNode } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './store/auth';
import { usePerm } from './hooks/usePerm';
import { useRbacBootstrap } from './hooks/useRbac';
import { routeAllowed } from './registry/access';
import { resolveLegacyTabRedirect } from './registry/features';
import { TabRouteProvider } from './hooks/useBccTabUrl';
import BasicLayout from './layouts/BasicLayout';
import { CONFIG_ROLES, MONITOR_ROLES, KB_ROLES } from './layouts/menus';
import Result403 from './components/Result403';
import NotFound from './components/NotFound';
import { LegacyInboundSettingsRedirect, LegacyMessagesRedirect, LegacySysSettingsRedirect, Loadable, Loading, RbacLoadError, ScreenGuard } from './_routeShells';
const Login = lazy(() => import('./pages/login'));
const Dashboard = lazy(() => import('./pages/dashboard'));
const Workbench = lazy(() => import('./pages/workbench'));
const Calls = lazy(() => import('./pages/calls'));
const Quality = lazy(() => import('./pages/quality'));
const Tickets = lazy(() => import('./pages/tickets'));
const Crm = lazy(() => import('./pages/crm'));
const KnowledgeKbManage = lazy(() => import('./pages/knowledge/kbmanage'));
const KnowledgeChat = lazy(() => import('./pages/knowledge/chat/NewChat'));
const KnowledgeChatHistory = lazy(() => import('./pages/knowledge/chat/History'));
const KnowledgeChatFeedback = lazy(() => import('./pages/knowledge/chat/Feedback'));
const Chat = lazy(() => import('./pages/chat'));
const ChatAnalytics = lazy(() => import('./pages/chat/Analytics'));
const ChatSettings = lazy(() => import('./pages/chat/Settings'));
const Bot = lazy(() => import('./pages/bot'));
const ChatBot = lazy(() => import('./pages/bot/ChatRobots'));
const AiCenter = lazy(() => import('./pages/aicenter'));
const LlmHistory = lazy(() => import('./pages/aicenter/LlmHistory'));
const UsageMonitor = lazy(() => import('./pages/aicenter/UsageMonitor'));
const PromptConsole = lazy(() => import('./pages/aicenter/PromptConsole'));
const LLMStudio = lazy(() => import('./pages/aicenter/LLMStudio'));
const Intent = lazy(() => import('./pages/intent'));
const Outbound = lazy(() => import('./pages/outbound'));
const OutboundSettings = lazy(() => import('./pages/outbound/Settings'));
const Report = lazy(() => import('./pages/report'));
const Satisfaction = lazy(() => import('./pages/satisfaction'));
const Account = lazy(() => import('./pages/system/Account'));
const AgentsPage = lazy(() => import('./pages/system/AgentsPage'));
const SystemRoutes = lazy(() => import('./pages/system/Routes'));
const IvrMenuEditor = lazy(() => import('./pages/system/IvrMenuEditor'));
const SkillGroups = lazy(() => import('./pages/system/SkillGroups'));
const AuditLogs = lazy(() => import('./pages/system/AuditLogs'));
const SipGateways = lazy(() => import('./pages/system/Gateways'));
const SipSettings = lazy(() => import('./pages/system/FsSettings'));
const TrunkAccounts = lazy(() => import('./pages/system/TrunkAccounts'));
const ComplianceSettings = lazy(() => import('./pages/system/ComplianceSettings'));
const SecuritySettings = lazy(() => import('./pages/system/SecuritySettings'));
const AudioFiles = lazy(() => import('./pages/system/AudioFiles'));
const EgressFlow = lazy(() => import('./pages/system/EgressFlow'));
const Collection = lazy(() => import('./pages/system/Collection'));
const TicketFieldConfig = lazy(() => import('./pages/system/TicketFieldConfig'));
const TicketTemplates = lazy(() => import('./pages/system/TicketTemplates'));
const Workflows = lazy(() => import('./pages/system/Workflows'));
const TicketConfigPanel = lazy(() => import('./pages/aicenter/TicketConfigPanel'));
const DataSource = lazy(() => import('./pages/system/DataSource'));
const DataSourceAPIs = lazy(() => import('./pages/system/DataSourceAPIs'));
const Workflow = lazy(() => import('./pages/workflow'));
const FlowManager = lazy(() => import('./pages/flow'));
const FlowDesigner = lazy(() => import('./pages/flow/Designer'));
const CollectFields = lazy(() => import('./pages/system/CollectFields'));
const Blacklist = lazy(() => import('./pages/calls/Blacklist'));
const Transcripts = lazy(() => import('./pages/calls/Transcripts'));
const QuickReplies = lazy(() => import('./pages/chat/QuickReplies'));
const Scripts = lazy(() => import('./pages/scripts'));
const ShiftManage = lazy(() => import('./pages/system/ShiftManage'));
const MonitorScreen = lazy(() => import('./pages/monitor/Screen'));
const TeamBoard = lazy(() => import('./pages/operations/TeamBoard'));
const DutyCommand = lazy(() => import('./pages/operations/DutyCommand'));
const DutyControl = lazy(() => import('./pages/operations/DutyControl'));
const Incidents = lazy(() => import('./pages/operations/Incidents'));
const OpsCenter = lazy(() => import('./pages/opscenter'));
const Voc = lazy(() => import('./pages/voc'));
const VocLabels = lazy(() => import('./pages/system/VocLabels'));
const AgentBaseSettings = lazy(() => import('./pages/system/AgentBaseSettings'));
const SatisfactionConfig = lazy(() => import('./pages/system/SatisfactionConfig'));
const Coach = lazy(() => import('./pages/coach'));
const AssistSettings = lazy(() => import('./pages/system/AssistSettings'));
const MarketingProductsPage = lazy(() => import('./pages/assist/MarketingProducts'));
const VoiceDefaults = lazy(() => import('./pages/bot/VoiceDefaults'));
const QualitySettings = lazy(() => import('./pages/quality/Settings'));
// 质检模板是独立页（不占质检页内 Tab）：它是配置面，与「实时质检/质检任务」等作业页职责不同
const QualityTemplates = lazy(() => import('./pages/quality/Templates'));
const CockpitSettings = lazy(() => import('./pages/opscenter/Settings'));
const DiagPage = lazy(() => import('./pages/monitor/Diag'));
const Notices = lazy(() => import('./pages/system/Notices'));
const TelephonyRouting = lazy(() => import('./pages/telephony/Routing'));
const SmsRecords = lazy(() => import('./pages/telephony/SmsRecords'));
const TicketingSettings = lazy(() => import('./pages/ticketing/Settings'));
const CustomerInsightsSettings = lazy(() => import('./pages/customer-insights/Settings'));
const OperationsSettings = lazy(() => import('./pages/operations/Settings'));
const TelephonySettings = lazy(() => import('./pages/telephony/Settings'));
/**
 * Canonical 路由（Feature Registry 驱动）：直连到现有页面组件。
 * 未拆分的 tab 页（quality/outbound/ops-center/coach 等）在 CANONICAL_REDIRECTS 里过渡到现有 query 形式，
 * Phase 3（Giant Tab 拆分）落地后再改为直连。
 */
/** canonical 子路由 → 页面初始 Tab（页面经 useBccTabUrl 读取，无需感知路径） */
const tabRoute = (tab: string, el: ReactNode) => <TabRouteProvider tab={tab}>{el}</TabRouteProvider>;

const CANONICAL_ROUTES: Array<{ path: string; el: ReactNode }> = [
  // 工作台（跨域聚合）
  { path: 'workbench/tasks', el: <Workflow /> },
  // 外呼
  { path: 'outbound/board', el: tabRoute('tasks', <Outbound />) },
  { path: 'outbound/jobs', el: tabRoute('tasks', <Outbound />) },
  { path: 'outbound/scenes', el: tabRoute('scenarios', <Outbound />) },
  { path: 'outbound/scenes/:sceneId', el: tabRoute('scenarios', <Outbound />) },
  { path: 'outbound/settings', el: <OutboundSettings /> },
  { path: 'outbound/settings/concurrency', el: <OutboundSettings /> },
  // 在线客服
  { path: 'chat/sessions', el: <Chat /> },
  { path: 'chat/quick-replies', el: <QuickReplies /> },
  { path: 'chat/bot', el: <ChatBot /> },
  { path: 'chat/analytics', el: <ChatAnalytics /> },
  { path: 'chat/settings', el: <ChatSettings /> },
  // 呼入
  { path: 'inbound/calls', el: <Calls /> },
  { path: 'inbound/transcripts', el: <Transcripts /> },
  { path: 'inbound/blacklist', el: <Blacklist /> },
  // 机器人
  { path: 'bot/robots', el: <Bot /> },
  { path: 'bot/robots/:id', el: <Bot /> },
  { path: 'bot/robots/:id/flow', el: <Bot /> },
  { path: 'bot/robots/:id/voice', el: <Bot /> },
  { path: 'bot/robots/:id/models', el: <Bot /> },
  { path: 'bot/chat-robots', el: <ChatBot /> },
  { path: 'bot/intents', el: <Intent /> },
  { path: 'bot/intents/ruleword', el: tabRoute('ruleword', <Intent />) },
  // 通用规则词表并入「意图识别」页签（/bot/ruleword 为旧地址直连，落到同一页签）
  { path: 'bot/ruleword', el: tabRoute('ruleword', <Intent />) },
  // 坐席辅助
  { path: 'assist/scripts', el: <Scripts /> },
  { path: 'assist/products', el: <MarketingProductsPage /> },
  { path: 'assist/settings', el: <AssistSettings /> },
  // 智能质检
  { path: 'quality/appeals', el: <Quality /> },
  { path: 'quality/sampling', el: <Quality /> },
  { path: 'quality/rules', el: <Quality /> },
  { path: 'quality/jobs', el: <Quality /> },
  { path: 'quality/realtime', el: <Quality /> },
  { path: 'quality/flow-reports', el: <Quality /> },
  { path: 'quality/analytics', el: <QualityTabRedirect /> },
  { path: 'quality/settings', el: <QualitySettings /> },
  { path: 'quality/templates', el: <QualityTemplates /> },
  // 知识库（WeKnora 为唯一知识来源）：只剩问答 / 管理 / 历史三个入口。
  // 自建的问答库与全文库此前已整体下线（页面、接口、菜单码、权限码一并摘除）——老地址不再重定向，
  // 由 `*` 兜底落 404 页（见文件末尾），不再把它们"顺手"接回某个现存页面。
  { path: 'knowledge/chat', el: <KnowledgeChat /> },
  { path: 'knowledge/chat/history', el: <KnowledgeChatHistory /> },
  { path: 'knowledge/feedback', el: <KnowledgeChatFeedback /> },
  { path: 'knowledge/kb', el: <KnowledgeKbManage /> },
  // 客户与工单
  { path: 'crm/customers', el: <Crm /> },
  { path: 'tickets/settings', el: <TicketingSettings /> },
  { path: 'tickets/settings/fields', el: tabRoute('fields', <TicketFieldConfig />) },
  { path: 'tickets/settings/templates', el: <TicketTemplates /> },
  { path: 'tickets/settings/routing', el: tabRoute('rules', <TicketFieldConfig />) },
  { path: 'tickets/settings/approval', el: <Workflows /> },
  { path: 'tickets/settings/auto-create', el: <TicketConfigPanel /> },
  // 智能演练
  { path: 'coach/:view?', el: <Coach /> },
  // 运营监控
  { path: 'operations/agents', el: tabRoute('agents', <OpsCenter />) },
  { path: 'operations/queues', el: tabRoute('queues', <OpsCenter />) },
  { path: 'operations/cockpit', el: tabRoute('cockpit', <OpsCenter />) },
  { path: 'operations/screen', el: <MonitorScreen /> },
  { path: 'operations/team', el: <TeamBoard /> },
  { path: 'operations/duty', el: <DutyCommand /> },
  { path: 'operations/duty/control', el: <DutyControl /> },
  { path: 'operations/incidents', el: <Incidents /> },
  { path: 'operations/shifts', el: <ShiftManage /> },
  { path: 'operations/settings', el: <OperationsSettings /> },
  { path: 'operations/settings/alerts', el: <CockpitSettings /> },
  { path: 'operations/settings/agents', el: <AgentBaseSettings /> },
  // 客户洞察
  { path: 'customer-insights/satisfaction', el: <Satisfaction /> },
  { path: 'customer-insights/voc', el: <Voc /> },
  { path: 'customer-insights/settings', el: <CustomerInsightsSettings /> },
  // 报表中心（三分组子菜单）
  { path: 'report/core', el: tabRoute('core', <Report />) },
  { path: 'report/ai', el: tabRoute('ai', <Report />) },
  { path: 'report/service', el: tabRoute('service', <Report />) },
  // 平台 · AI
  { path: 'platform/ai/providers', el: <AiCenter group="providers" /> },
  { path: 'platform/ai/prompts', el: <PromptConsole /> },
  { path: 'platform/ai/usage', el: <UsageMonitor /> },
  { path: 'platform/ai/cost', el: <AiCenter group="cost" /> },
  { path: 'platform/ai/studio', el: <LLMStudio /> },
  // 平台 · 通信
  { path: 'platform/telephony/gateways', el: <SipGateways /> },
  { path: 'platform/telephony/sip', el: <SipSettings /> },
  { path: 'platform/telephony/trunks', el: <TrunkAccounts /> },
  { path: 'platform/telephony/routing', el: <TelephonyRouting /> },
  { path: 'platform/telephony/routing/ivr', el: tabRoute('ivr', <TelephonyRouting />) },
  { path: 'platform/telephony/audio', el: <AudioFiles /> },
  { path: 'platform/telephony/settings', el: <TelephonySettings /> },
  { path: 'platform/telephony/capacity', el: tabRoute('media', <TelephonySettings />) },
  { path: 'platform/telephony/whitelist', el: tabRoute('whitelist', <TelephonySettings />) },
  { path: 'platform/telephony/sms', el: <SmsRecords /> },
  // 平台 · 流程 / 数据采集
  { path: 'platform/workflow/flows', el: <FlowManager /> },
  { path: 'platform/workflow/approvals', el: <Workflows /> },
  { path: 'platform/data-collection/fields', el: <CollectFields /> },
  { path: 'platform/data-collection/rules', el: <Collection /> },
  // 系统
  { path: 'system/identity/users', el: <Account view="users" /> },
  { path: 'system/identity/agents', el: <AgentsPage /> },
  { path: 'system/identity/agents/skill-groups', el: tabRoute('skill-groups', <AgentsPage />) },
  { path: 'system/identity/orgs', el: <Account view="orgs" /> },
  { path: 'system/identity/roles', el: <Account view="roles" /> },
  // 开放平台已整体下线：应用/密钥管理页、`/api/open/v1/*` 与菜单码一并摘除，故不再挂路由。
  { path: 'system/integration/data-sources', el: <DataSource /> },
  { path: 'system/compliance/settings', el: <ComplianceSettings /> },
  { path: 'system/compliance/security', el: <SecuritySettings /> },
  { path: 'system/compliance/egress', el: <EgressFlow /> },
  { path: 'system/audit/logs', el: <AuditLogs /> },
  { path: 'system/audit/ai', el: <LlmHistory /> },
  { path: 'system/diagnostics', el: <DiagPage /> },
  { path: 'system/notices', el: <Notices /> },
];

/** Canonical → 现有 query 形式（报表按域定位到分组；tab 页已改为 TabRouteProvider 直连） */
const CANONICAL_REDIRECTS: Array<{ path: string; to: string }> = [
  // 进线路由：原呼入域两个独立配置页（/system/* 为更早的地址）合并为「进线路由」两 Tab，旧地址落到对应 Tab；
  // 原第三 Tab「接线时段」已随该能力下线，其旧地址落到进线路由页（不落仪表盘）
  { path: 'inbound/routes', to: '/platform/telephony/routing' },
  { path: 'system/routes', to: '/platform/telephony/routing' },
  { path: 'inbound/ivr', to: '/platform/telephony/routing/ivr' },
  { path: 'system/ivr-menus', to: '/platform/telephony/routing/ivr' },
  { path: 'inbound/schedules', to: '/platform/telephony/routing' },
  { path: 'system/schedules', to: '/platform/telephony/routing' },
  { path: 'platform/telephony/routing/schedules', to: '/platform/telephony/routing' },
  // 技能组：原呼入域配置项与更早的 /system/skill-groups，现为「坐席」页内 Tab
  { path: 'inbound/skill-groups', to: '/system/identity/agents/skill-groups' },
  { path: 'system/skill-groups', to: '/system/identity/agents/skill-groups' },
  { path: 'outbound/analytics', to: '/report?group=service' },
  { path: 'inbound/analytics', to: '/report?group=core' },
  { path: 'bot/analytics', to: '/report?group=ai' },
  { path: 'tickets/analytics', to: '/report?group=core' },
  { path: 'operations/analytics', to: '/report?group=core' },
  { path: 'assist/analytics', to: '/report?group=core' },
  { path: 'quality/reports', to: '/quality/sampling?tab=reports' },
  { path: 'quality/samples', to: '/quality/sampling?tab=samples' },
  { path: 'quality/mine', to: '/quality/sampling' },
  { path: 'customer-insights/settings/satisfaction', to: '/customer-insights/settings?tab=satisfaction' },
  { path: 'customer-insights/settings/voc-labels', to: '/customer-insights/settings?tab=voc-labels' },
  // 知识库（问答库 / 全文库）的老地址一律不登记重定向：页面与菜单码已整体下线，
  // 深链进来由 `*` 落 404 页，而不是被"顺手"接到某个现存页面（否则用户会以为功能还在、只是换了地方）。
  { path: 'platform/workflow/instances', to: '/platform/workflow/flows' },
  { path: 'system/identity/security', to: '/system/compliance/security' },
  // 工单看板已并入「工单」页内视图（列表/看板切换），旧地址回落到工单列表
  { path: 'tickets/board', to: '/tickets' },
  // 「工单草稿」页已下线：草稿并入工单列表（状态筛选「草稿」+ 行内提交），旧地址落到该筛选
  { path: 'tickets/drafts', to: '/tickets?status=draft' },
  // 一对一旧地址：原「AI 中台」三页拆到「AI 能力」后，两个单义旧地址收敛为静态重定向
  //（/aicenter 本身是一对多旧地址，保持直连，见上方 Route 注释）
  { path: 'aicenter/prompt-console', to: '/platform/ai/prompts' },
  { path: 'aicenter/llm-studio', to: '/platform/ai/studio' },
  // 呼入域旧地址：短信留痕迁入通信能力后，该地址此前既无 Route 也无重定向（静默落仪表盘）
  { path: 'inbound/sms', to: '/platform/telephony/sms' },
];

/**
 * 「质检分析」旧 Tab 深链兜底：实时质检 / 流程质检已从页内 Tab 提升为独立二级菜单，
 * 旧地址（/quality?tab=realtime、/quality/analytics?tab=flow-reports 等）按 Registry 的 Tab 规则
 * 落到新页；其余 Tab（综合报告/统计）仍由 Quality 页内处理。
 */
function QualityTabRedirect() {
  const { pathname, search } = useLocation();
  const tab = new URLSearchParams(search).get('tab');
  if (tab === 'realtime' || tab === 'flow-reports') {
    const target = resolveLegacyTabRedirect(pathname, tab);
    if (target) return <Navigate to={target.route} replace />;
  }
  return <Quality />;
}

/**
 * 角色-路由矩阵：命中前缀后按**权限码**放行（角色白名单只是权限码的粗粒度代理，
 * 自定义角色可能只被授予权限码而无对应角色码），判定实现唯一在 `registry/access.ts::routeAllowed`。
 *
 * 两条纪律（由 `scripts/check-nav-perms.mjs` 门禁强制）：
 * 1. **每条都必须登记 perms**：`usePerm([])` 恒为 true，早期写成 `!roleOk && !permHit` 的取反，
 *    让未登记权限码的前缀（/system、/platform、各 Settings Landing）对全角色放行，
 *    这是「坐席能进客户洞察设置、进去后处处 403」的根因；
 * 2. **perms 必须覆盖该前缀下所有菜单码**：某角色能看见菜单却过不了守卫，就是「可见但 403」。
 *
 * 匹配口径：**最长前缀命中者生效**——允许更具体的路径覆盖更宽的前缀
 *（如 /system/flows 挂 CONFIG_ROLES 覆盖 /system 的 admin-only）。
 * 知识库 kbAccess 节点：rbac.roles 含 kb.* 分权角色时放行（与菜单裁剪同口径）。
 */
const roleRouteMap: Array<{ prefix: string; roles: string[]; perms?: string[]; kbAccess?: boolean }> = [
  // ── 呼入：设置 Landing 已拆解，/inbound/settings 仅作过渡重定向（按 ?tab= 落到新归属） ──
  { prefix: '/inbound/settings', roles: ['admin', 'supervisor'], perms: ['inbound.settings', 'inbound.blacklist'] },
  { prefix: '/inbound/blacklist', roles: ['admin', 'supervisor', 'agent'], perms: ['inbound.blacklist'] },
  // 技能组/呼叫路由的旧地址：页面已迁（技能组→坐席页内 Tab、呼叫路由→进线路由），此两前缀仅作过渡
  { prefix: '/inbound/ivr', roles: ['admin', 'supervisor'], perms: ['inbound.ivr'] },
  { prefix: '/inbound/routes', roles: ['admin'], perms: ['inbound.routes'] },
  { prefix: '/inbound/skill-groups', roles: ['admin'], perms: ['inbound.skill-groups'] },
  { prefix: '/inbound/sms', roles: ['admin', 'supervisor'], perms: ['inbound.sms'] },
  { prefix: '/inbound', roles: ['admin', 'supervisor', 'agent'], perms: ['inbound.calls', 'inbound.transcripts'] },
  // ── 在线客服 ──
  { prefix: '/chat/bot', roles: CONFIG_ROLES, perms: ['chat.bot'] },
  // 在线客服设置为 sys_setting 写端（PUT /sys-settings 需 syssetting.manage，admin-only），
  // 故守卫与菜单同收 admin，避免主管可见却保存 403（与质检设置/语音行为默认同口径）
  { prefix: '/chat/settings', roles: ['admin'], perms: ['chat.settings'] },
  { prefix: '/chat/sessions', roles: ['admin', 'supervisor', 'agent'], perms: ['chat.sessions'] },
  { prefix: '/chat/quick-replies', roles: ['admin', 'supervisor', 'agent'], perms: ['chat.quickreplies'] },
  { prefix: '/chat', roles: ['admin', 'supervisor', 'agent'], perms: ['chat.sessions', 'chat.quickreplies'] },
  // ── 机器人 ──
  // 在线机器人为 chat 通道 bot（权限码仍为 chat.bot）：与语音机器人同域并列，菜单/守卫同码
  { prefix: '/bot/chat-robots', roles: CONFIG_ROLES, perms: ['chat.bot'] },
  // 意图识别 = 意图词表 + 通用规则词表 + 统计分析 + 意图测试台（页签各按自己的码裁剪，故守卫取码组）
  { prefix: '/bot/intents', roles: ['admin'], perms: ['bot.intents', 'bot.ruleword'] },
  { prefix: '/bot/voice-defaults', roles: ['admin'], perms: ['bot.voice-defaults'] },
  { prefix: '/bot/robots', roles: CONFIG_ROLES, perms: ['bot.robots'] },
  // 通用规则词表已并入意图识别页签；写端为 PUT /sys-settings（syssetting.manage，admin-only），
  // 故旧地址守卫与菜单同收 admin——避免主管「看得见、存不了」（403）
  { prefix: '/bot/ruleword', roles: ['admin'], perms: ['bot.ruleword'] },
  { prefix: '/bot', roles: CONFIG_ROLES, perms: ['bot.robots', 'bot.ruleword', 'chat.bot'] },
  // ── 坐席辅助：话术库/营销产品 各自成页，坐席辅助设置 admin-only（营业厅已退役）──
  { prefix: '/assist/scripts', roles: CONFIG_ROLES, perms: ['assist.scripts'] },
  { prefix: '/assist/products', roles: ['admin'], perms: ['assist.products'] },
  { prefix: '/assist/settings', roles: ['admin'], perms: ['assist.settings'] },
  { prefix: '/assist', roles: ['admin'], perms: ['assist.products', 'assist.settings'] },
  // ── 质检：坐席经「质检详情」按权限裁剪（报告/申诉为本人口径），管理面各项 admin-only ──
  { prefix: '/quality/appeals', roles: ['admin', 'supervisor', 'agent'], perms: ['quality.reports'] },
  { prefix: '/quality/sampling', roles: ['admin'], perms: ['quality.sampling', 'quality.reports', 'quality.flow-reports'] },
  { prefix: '/quality/templates', roles: ['admin'], perms: ['quality.templates'] },
  { prefix: '/quality/rules', roles: ['admin'], perms: ['quality.rules'] },
  { prefix: '/quality/jobs', roles: ['admin'], perms: ['quality.jobs'] },
  { prefix: '/quality/analytics', roles: ['admin'], perms: ['quality.analytics'] },
  { prefix: '/quality/realtime', roles: ['admin'], perms: ['quality.realtime'] },
  { prefix: '/quality/flow-reports', roles: ['admin'], perms: ['quality.flow-reports'] },
  { prefix: '/quality/settings', roles: ['admin'], perms: ['quality.settings'] },
  { prefix: '/quality', roles: CONFIG_ROLES, perms: ['quality.reports'] },
  // ── 知识库（WeKnora 为唯一知识来源：问答 / 管理 / 历史三个入口） ──
  // 自建的问答库与全文库此前已整体下线：它们的页面、菜单码与守卫条目一并摘除，
  // 旧前缀不再登记守卫、也不再挂路由——深链进来由 `*` 落 404 页，不会被某个泛前缀"接住"。
  // 知识问答 / 问答历史：两条页面码互为对方（持其一即可深链两页），故一条前缀守卫即可覆盖
  { prefix: '/knowledge/chat', roles: ['admin'], perms: ['knowledge.chat', 'knowledge.chat.history'] },
  // 反馈处理（WeKnora 点踩处理面）：独立页面码 knowledge.feedback，只对管理员开放——
  // 看的是全租户的点踩意见与处理结论，属于管理动作，不是坐席查资料的公共面。
  { prefix: '/knowledge/feedback', roles: ['admin'], perms: ['knowledge.feedback'] },
  // 知识管理（WeKnora 文档库管理面）：独立页面码 knowledge.kbmanage（不再复用问答库管理那枚已下线的码）。
  // 单列一条更具体的前缀，让「这页凭什么进」与菜单同码（不含 kbAccess 兜底）：
  // 知识库分权角色（kb.admin/editor/branch）在 permspec 里被直接授予该码，走 perms 正常放行。
  { prefix: '/knowledge/kb', roles: ['admin'], perms: ['knowledge.kbmanage'] },
  // 知识库域级通道：domain 内其余地址的兜底（kbAccess 与菜单裁剪同口径）。
  { prefix: '/knowledge', roles: ['admin'], perms: ['knowledge', 'knowledge.kbmanage'], kbAccess: true },
  // ── 客户与工单 ──
  { prefix: '/tickets/settings', roles: ['admin'], perms: ['ticketing.settings', 'ticketing.settings.fields', 'ticketing.settings.templates', 'ticketing.settings.routing', 'ticketing.settings.approval', 'ticketing.settings.auto-create'] },
  { prefix: '/tickets', roles: ['admin', 'supervisor', 'agent'], perms: ['ticketing.tickets'] },
  { prefix: '/crm', roles: ['admin', 'supervisor'], perms: ['ticketing.customers'] },
  // ── 智能演练（逐叶子登记页面码：题库为独立页面 coach.bank，演练与记录为另两页） ──
  { prefix: '/coach/bank', roles: ['admin'], perms: ['coach.bank'] },
  { prefix: '/coach', roles: ['admin', 'supervisor', 'agent'], perms: ['coach.practice', 'coach.history'] },
  // ── 运营监控（admin+supervisor） ──
  { prefix: '/operations/settings', roles: ['admin'], perms: ['operations.settings', 'operations.settings.alerts', 'operations.settings.agents'] },
  { prefix: '/operations/agents', roles: MONITOR_ROLES, perms: ['operations.agents'] },
  { prefix: '/operations/queues', roles: MONITOR_ROLES, perms: ['operations.queues'] },
  { prefix: '/operations/cockpit', roles: MONITOR_ROLES, perms: ['operations.cockpit'] },
  { prefix: '/operations/screen', roles: MONITOR_ROLES, perms: ['operations.screen'] },
  { prefix: '/operations/team', roles: MONITOR_ROLES, perms: ['operations.agents'] },
  { prefix: '/operations/duty', roles: MONITOR_ROLES, perms: ['operations.screen'] },
  { prefix: '/operations/incidents', roles: MONITOR_ROLES, perms: ['operations.screen'] },
  { prefix: '/operations/shifts', roles: MONITOR_ROLES, perms: ['operations.shifts'] },
  { prefix: '/operations', roles: MONITOR_ROLES, perms: ['operations'] },
  { prefix: '/ops-center/settings', roles: ['admin'], perms: ['operations.settings.alerts'] },
  { prefix: '/ops-center', roles: MONITOR_ROLES, perms: ['operations.cockpit'] },
  { prefix: '/shift', roles: MONITOR_ROLES, perms: ['operations.shifts'] },
  { prefix: '/monitor', roles: MONITOR_ROLES, perms: ['operations', 'operations.screen'] },
  // ── 客户洞察：设置 Landing 当前仅 admin 持有子码 ──
  { prefix: '/customer-insights/settings', roles: ['admin'], perms: ['customer-insights.settings', 'customer-insights.settings.satisfaction', 'customer-insights.settings.voc-labels'] },
  { prefix: '/customer-insights/voc', roles: MONITOR_ROLES, perms: ['customer-insights.voc'] },
  { prefix: '/customer-insights/satisfaction', roles: ['admin', 'supervisor', 'agent'], perms: ['customer-insights.satisfaction'] },
  { prefix: '/customer-insights', roles: ['admin', 'supervisor', 'agent'], perms: ['customer-insights.satisfaction'] },
  { prefix: '/voc', roles: MONITOR_ROLES, perms: ['customer-insights.voc'] },
  // 满意度设置（评价域配置，独立于 /system 与菜单归属同构）：随「满意度设置」页 customer-insights.settings.satisfaction
  { prefix: '/satisfaction/config', roles: ['admin'], perms: ['customer-insights.settings.satisfaction'] },
  { prefix: '/satisfaction', roles: ['admin', 'supervisor', 'agent'], perms: ['customer-insights.satisfaction'] },
  // ── 报表中心：读端无权限点（历史登记 Q-2026-038，台账已归档不入库），入口由菜单码收敛到 admin+supervisor ──
  { prefix: '/report', roles: MONITOR_ROLES, perms: ['analytics.report'] },
  // ── 外呼 ──
  // 外呼设置为 sys_setting 写端（PUT /sys-settings 需 syssetting.manage，admin-only），
  // 独立菜单码与守卫，避免主管可见却保存 403
  { prefix: '/outbound/settings', roles: ['admin'], perms: ['outbound.settings'] },
  // 任务/场景各有独立菜单码（父码 outbound 只覆盖号码看板这类无独立菜单码的外呼子页）：
  // 只写父码会让「只勾了外呼任务」的角色被拦在门外
  { prefix: '/outbound', roles: ['admin', 'supervisor'], perms: ['outbound', 'outbound.jobs', 'outbound.scenes'] },
  // ── 平台：AI / 通信 / 流程 / 数据采集（admin-only；业务流程页对主管开放） ──
  { prefix: '/platform/workflow/flows', roles: CONFIG_ROLES, perms: ['workflow.flows'] },
  // 进线路由（通信能力）：两个 Tab 各有自己的菜单码——主管持 inbound.ivr（入口对「任一子码」可见），
  // 呼叫路由为 admin-only；守卫按最长前缀逐 Tab 对齐，否则会出现「菜单可见但点进去 403」
  { prefix: '/platform/telephony/routing/ivr', roles: ['admin', 'supervisor'], perms: ['inbound.ivr'] },
  { prefix: '/platform/telephony/routing', roles: ['admin', 'supervisor'], perms: ['inbound.routes', 'inbound.ivr'] },
  // 短信记录（通信能力）：该页的读端（GET /sms-records）随页面码 inbound.sms，守卫与菜单同码同口径。
  // 注意：不能把 inbound.sms 并入下面的 /platform 父守卫 perms——主管持有该码，
  // 并入等于对整片平台页放行（门禁会报「菜单不可见但守卫放行」），故只在此具体前缀登记。
  { prefix: '/platform/telephony/sms', roles: ['admin', 'supervisor'], perms: ['inbound.sms'] },
  { prefix: '/platform', roles: ['admin'], perms: ['ai.providers', 'ai.prompts', 'ai.usage', 'ai.cost', 'ai.studio', 'telephony.gateways', 'telephony.sip', 'telephony.trunks', 'telephony.audio', 'telephony.settings', 'telephony.capacity', 'workflow.approvals', 'data-collection.fields', 'data-collection.rules'] },
  // ── 系统治理（admin；组织架构/故障诊断/业务流程对主管开放，见上/下） ──
  { prefix: '/system/flows', roles: CONFIG_ROLES, perms: ['workflow.flows'] },
  { prefix: '/system/identity/orgs', roles: MONITOR_ROLES, perms: ['identity.orgs'] },
  { prefix: '/system/orgs', roles: MONITOR_ROLES, perms: ['identity'] },
  { prefix: '/system/diag', roles: MONITOR_ROLES, perms: ['diagnostics.diag'] },
  { prefix: '/system/diagnostics', roles: MONITOR_ROLES, perms: ['diagnostics.diag'] },
  { prefix: '/system/notices', roles: ['admin', 'supervisor'], perms: ['notice.manage'] },
  // 逐叶子登记（勿写父码 system/identity：主管持这两个父码，写父码等于把整个系统治理面对主管放行）
  // 坐席页含「技能组」Tab（码为 inbound.skill-groups，仅 admin）——显式登记避免 /system 兜底把口径含糊掉
  { prefix: '/system/identity/agents', roles: ['admin'], perms: ['identity.agents', 'inbound.skill-groups'] },
  { prefix: '/system', roles: ['admin'], perms: ['identity.users', 'identity.agents', 'identity.roles', 'integration.data-sources', 'compliance.settings', 'compliance.security', 'compliance.egress', 'audit.logs', 'audit.ai'] },
  // 账号与组织（用户/坐席 admin，组织架构 admin+supervisor 页内裁剪）
  { prefix: '/account', roles: MONITOR_ROLES, perms: ['identity'] },
  // ── legacy 前缀（页面已迁 canonical，这些深链仍需与菜单同口径） ──
  { prefix: '/calls/blacklist', roles: ['admin', 'supervisor', 'agent'], perms: ['inbound.blacklist'] },
  { prefix: '/calls/transcripts', roles: ['admin', 'supervisor', 'agent'], perms: ['inbound.transcripts'] },
  { prefix: '/calls', roles: ['admin', 'supervisor', 'agent'], perms: ['inbound.calls'] },
  { prefix: '/workflow', roles: ['admin', 'supervisor', 'agent'], perms: ['workflow.inbox'] },
  { prefix: '/workbench/tasks', roles: ['admin', 'supervisor', 'agent'], perms: ['workflow.inbox'] },
  { prefix: '/scripts', roles: CONFIG_ROLES, perms: ['assist.scripts'] },
  { prefix: '/aicenter', roles: ['admin'], perms: ['ai'] },
  { prefix: '/intent', roles: ['admin'], perms: ['bot.intents'] },
  { prefix: '/chatbot', roles: CONFIG_ROLES, perms: ['chat.bot'] },
];

function RequireAuth({ children }: { children: JSX.Element }) {
  const token = useAuthStore((s) => s.token);
  const role = useAuthStore((s) => s.user?.role) || 'agent';
  const roles = useAuthStore((s) => s.rbac?.roles) || [];
  const hasKBRole = roles.some((r) => KB_ROLES.includes(r));
  const { pathname } = useLocation();
  // RBAC 上下文必须先于判据加载：它原本只由侧栏（在守卫之内）拉取，
  // 于是「凭权限码才能进」的页面对非 admin 会首帧判 403 → 子树不挂载 → 上下文永远加载不出来 → 永久 403
  const { loaded, error, retry } = useRbacBootstrap();
  // 最长前缀命中者生效：更具体的白名单覆盖更宽的拒绝（如 /system/flows 宽于 /system）
  const matched = roleRouteMap
    .filter((m) => pathname === m.prefix || pathname.startsWith(`${m.prefix}/`))
    .sort((a, b) => b.prefix.length - a.prefix.length)[0];
  // 必须无条件调用——条件化会使本组件 hook 数量随路径变化（/dashboard 等白名单外页
  // 0 次 vs 权限页 1 次），React 抛 hooks 数量不一致错误并卸载整树，表现即跳转白屏。
  const permHit = usePerm(matched?.perms);
  if (!token) return <Navigate to="/login" replace />;
  // 上下文未就绪时给加载态而不是 403：权限码判据此刻不可判，等上下文到了再判；
  // 拉取失败时给错误态与重试入口，而不是无限转圈（判据仍不可判，故不放行）
  if (matched && !loaded && role !== 'admin') {
    return error ? <RbacLoadError message={error} onRetry={retry} /> : <Loading />;
  }
  if (!routeAllowed(matched, { role, permHit, hasKBRole })) return <Result403 />;
  return children;
}

export default function App() {
  return (
      <Routes>
      <Route
        path="/login"
        element={
          <Loadable>
            <Login />
          </Loadable>
        }
      />
      {/* 监控大屏：独立暗色全屏页，不套管理台布局；支持 ?token= 免登 */}
      <Route
        path="/monitor/screen"
        element={
          <ScreenGuard>
            <Loadable>
              <MonitorScreen />
            </Loadable>
          </ScreenGuard>
        }
      />
      <Route
        path="/"
        element={
          <RequireAuth>
            <BasicLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Loadable><Dashboard /></Loadable>} />
        <Route path="workbench" element={<Loadable><Workbench /></Loadable>} />
        <Route path="calls" element={<Loadable><Calls /></Loadable>} />
        <Route path="calls/blacklist" element={<Loadable><Blacklist /></Loadable>} />
        <Route path="calls/transcripts" element={<Loadable><Transcripts /></Loadable>} />
        <Route path="scripts" element={<Loadable><Scripts /></Loadable>} />
        <Route path="quality" element={<Loadable><QualityTabRedirect /></Loadable>} />
        <Route path="tickets" element={<Loadable><Tickets /></Loadable>} />
        <Route path="crm" element={<Loadable><Crm /></Loadable>} />
        {/* 知识库（自建的问答库与全文库）的页面与路由已整体下线：
            这些老地址不再挂 Route、也不做重定向，一律由 `*` 落 404 页（见文件末尾）。 */}
        <Route path="chat" element={<Loadable><Chat /></Loadable>} />
        {/* 旧运营监控单页路由重定向到运营中心对应页签（保留深链兼容） */}
        <Route path="monitor/agents" element={<Navigate to="/ops-center?tab=agents" replace />} />
        <Route path="monitor/queues" element={<Navigate to="/ops-center?tab=queues" replace />} />
        <Route path="monitor/diag" element={<Navigate to="/system/diag" replace />} />
        <Route path="ops-center" element={<Loadable><OpsCenter /></Loadable>} />
        <Route path="shift" element={<Loadable><ShiftManage /></Loadable>} />
        <Route path="bot" element={<Loadable><Bot /></Loadable>} />
        <Route path="chatbot" element={<Loadable><ChatBot /></Loadable>} />
        <Route path="aicenter" element={<Loadable><AiCenter /></Loadable>} />
        <Route path="aicenter/llm-history" element={<Loadable><LlmHistory /></Loadable>} />
        {/* /aicenter/prompt-console 与 /aicenter/llm-studio 是**一对一**旧地址（Registry 里各自被唯一一条
            有 nav 的 Feature 认领），按 features.ts 的规则收敛为 CANONICAL_REDIRECTS 里的静态重定向，
            不再各挂一份实页（同一页面两套 URL 都会让页头名解析落空）。
            /aicenter/llm-history 必须保留直连：该地址被「调用监控」与「AI 调用审计」**同时**登记为旧位置
            （一对多），改重定向等于替 Registry 单方面选边。 */}
        <Route path="intent" element={<Loadable><Intent /></Loadable>} />
        <Route path="outbound" element={<Loadable><Outbound /></Loadable>} />
        <Route path="report" element={<Loadable><Report /></Loadable>} />
        <Route path="cockpit" element={<Navigate to="/ops-center?tab=cockpit" replace />} />
        <Route path="voc" element={<Loadable><Voc /></Loadable>} />
        <Route path="satisfaction" element={<Loadable><Satisfaction /></Loadable>} />
        <Route path="account" element={<Loadable><Account /></Loadable>} />
        {/* 旧「用户与组织」地址：账号与组织四页已各自独立成页，旧地址必须落到对应新页
            （/account 是一对多旧地址，不按 ?tab= 分发，指向它只会落到默认的组织视图） */}
        <Route path="system" element={<Navigate to="/system/identity/users" replace />} />
        {/* 旧呼入设置 Landing：按 ?tab= 落到拆解后的新归属（页面已删，只留过渡） */}
        <Route path="inbound/settings" element={<Loadable><LegacyInboundSettingsRedirect /></Loadable>} />
        {/* 旧系统设置 Landing：同上（此前既无 Route 也未登记重定向 → 命中 * 静默落仪表盘） */}
        <Route path="system/sys-settings" element={<Loadable><LegacySysSettingsRedirect /></Loadable>} />
        <Route path="system/ivr-menus/:id" element={<Loadable><IvrMenuEditor /></Loadable>} />
        <Route path="system/audit-logs" element={<Loadable><AuditLogs /></Loadable>} />
        <Route path="system/voc-labels" element={<Loadable><VocLabels /></Loadable>} />
        <Route path="system/sip-gateways" element={<Loadable><SipGateways /></Loadable>} />
        <Route path="system/sip-settings" element={<Loadable><SipSettings /></Loadable>} />
        <Route path="system/trunk-accounts" element={<Loadable><TrunkAccounts /></Loadable>} />
        <Route path="system/audio-files" element={<Loadable><AudioFiles /></Loadable>} />
        <Route path="system/agent-base-settings" element={<Loadable><AgentBaseSettings /></Loadable>} />
        <Route path="satisfaction/config" element={<Loadable><SatisfactionConfig /></Loadable>} />
        <Route path="system/egress" element={<Loadable><EgressFlow /></Loadable>} />
        <Route path="system/collection" element={<Loadable><Collection /></Loadable>} />
        <Route path="system/ticket-fields" element={<Loadable><TicketFieldConfig /></Loadable>} />
        <Route path="system/ticket-templates" element={<Loadable><TicketTemplates /></Loadable>} />
        <Route path="system/workflows" element={<Loadable><Workflows /></Loadable>} />
        <Route path="system/flows" element={<Loadable><FlowManager /></Loadable>} />
        <Route path="system/flows/designer/:id" element={<Loadable><FlowDesigner /></Loadable>} />
        <Route path="system/collect-fields" element={<Loadable><CollectFields /></Loadable>} />
        <Route path="system/data-sources" element={<Loadable><DataSource /></Loadable>} />
        <Route path="system/data-sources/:id/apis" element={<Loadable><DataSourceAPIs /></Loadable>} />
        {/* 开放平台（应用/密钥管理页与其对外接口）已整体下线：旧地址不挂 Route、也不做重定向，一律落 `*` 的 404 页。 */}
        <Route path="workflow" element={<Loadable><Workflow /></Loadable>} />
        {/* 旧「消息中心」（/workbench/messages）：已并入待办中心「通知/公告」两页签，按 ?tab= 落对应页签
            （通知铃的「查看全部…」曾经指向这里，旧书签与深链一并兼容） */}
        <Route path="workbench/messages" element={<Loadable><LegacyMessagesRedirect /></Loadable>} />
        <Route path="assist/config" element={<Loadable><AssistSettings /></Loadable>} />
        <Route path="bot/voice-defaults" element={<Loadable><VoiceDefaults /></Loadable>} />
        <Route path="ops-center/settings" element={<Loadable><CockpitSettings /></Loadable>} />
        <Route path="system/orgs" element={<Navigate to="/system/identity/orgs" replace />} />
        <Route path="system/diag" element={<Loadable><DiagPage /></Loadable>} />
        {/* Canonical 路由（Feature Registry 驱动）：直连组件 + tab 过渡重定向 */}
        {CANONICAL_ROUTES.map((r) => (
          <Route key={r.path} path={r.path} element={<Loadable>{r.el}</Loadable>} />
        ))}
        {CANONICAL_REDIRECTS.map((r) => (
          <Route key={r.path} path={r.path} element={<Navigate to={r.to} replace />} />
        ))}
        {/* 未登记的地址一律落 404 页（带侧栏的壳内落点）：
            下线功能的老地址（/kb、/knowledge/*、/system/integration/open-platform 等）不挂 Route、
            也不做重定向，就靠这一条显式告诉用户「这个地址不存在或已下线」，而不是静默跳回仪表盘。
            注意：这条必须在布局内（`/` 的子路由）——放布局外或换成 Navigate 都会让老地址静默落首页。 */}
        <Route path="*" element={<NotFound />} />
      </Route>
      </Routes>
  );
}
